/**
 * Comments program hook — command scheduler.
 *
 * Figma authority: R4VYIdXEv7HYIXtpDMEvHY, node 38:2510
 *
 * Elm-ish architecture: this is the ONLY place that combines:
 * - useState/useReducer for state management
 * - dispatch for message handling
 * - update() for pure state transitions
 * - runEffect() for side effects (outside the reducer)
 *
 * CORRECTION01 final architecture:
 * - Reducer maintains its own pending queue with state.nextEffectId
 * - Domain messages preserve sibling commands (don't replace queue)
 * - LoadStarted replaces queue (cancels previous load)
 * - EffectFinished removes only acknowledged effect
 * - No module-level mutable state
 * - Each effect has its own AbortController
 * - Completed effects are removed from active map
 * - Effects removed from queue are aborted (obsolete)
 */
import { useReducer, useEffect, useCallback, useRef } from 'react';
import { initialModel, type CommentsModel } from './model';
import type { CommentsMsg } from './messages';
import { update, type CommentsEffect } from './update';
import { runEffect, type CommentsEffectContext, type CommentsDependencies } from './effects';

export interface UseCommentsProgramProps {
  readonly dependencies: CommentsDependencies;
}

export interface UseCommentsProgramResult {
  readonly model: CommentsModel;
  readonly start: (postId: string, loadId: number) => void;
  readonly changeDraft: (draft: string) => void;
  readonly send: () => void;
  readonly like: (commentId: string) => void;
}

// =============================================================================
// Pending effect with unique identity
// =============================================================================

interface PendingEffect {
  readonly id: number;
  readonly effect: CommentsEffect;
}

interface TrackedEffect {
  readonly effect: CommentsEffect;
  readonly controller: AbortController;
  readonly promise: Promise<void>;
}

// =============================================================================
// Program state — pure, all state in one place
// =============================================================================

type ProgramState = {
  readonly model: CommentsModel;
  readonly pendingEffects: readonly PendingEffect[];
  readonly nextEffectId: number;
};

const initialState: ProgramState = {
  model: initialModel,
  pendingEffects: [],
  nextEffectId: 1,
};

/**
 * Internal action types — combines domain messages with scheduler acknowledgements.
 */
type ProgramAction =
  | { readonly type: 'DomainMessage'; readonly message: CommentsMsg }
  | { readonly type: 'EffectFinished'; readonly id: number };

/**
 * Append effects to pending queue with deterministic IDs from state.nextEffectId.
 * Pure — no module-level mutable state.
 */
function appendEffects(
  pendingEffects: readonly PendingEffect[],
  nextEffectId: number,
  effects: readonly CommentsEffect[]
): { readonly pendingEffects: readonly PendingEffect[]; readonly nextEffectId: number } {
  const newPending: PendingEffect[] = effects.map((effect, index) => ({
    id: nextEffectId + index,
    effect,
  }));
  return {
    pendingEffects: [...pendingEffects, ...newPending],
    nextEffectId: nextEffectId + effects.length,
  };
}

/**
 * The program reducer — combines domain state and scheduler queue.
 *
 * Purity invariants:
 * - No module-level mutable state
 * - All ID allocation derives from state.nextEffectId
 *
 * Concurrency invariants:
 * - Domain messages update the model
 * - Effects from the update are appended to the pending queue
 * - EffectFinished removes only the acknowledged effect
 * - One completion cannot cancel a sibling
 */
function programReducer(
  state: ProgramState,
  action: ProgramAction
): ProgramState {
  switch (action.type) {
    case 'DomainMessage': {
      const result = update(state.model, action.message);
      // LoadStarted REPLACES the queue (cancel any previous load)
      // Other messages APPEND to preserve sibling commands
      if (action.message.type === 'LoadStarted') {
        const fresh = appendEffects([], state.nextEffectId, result.effects);
        return {
          model: result.model,
          pendingEffects: fresh.pendingEffects,
          nextEffectId: fresh.nextEffectId,
        };
      }
      const appended = appendEffects(state.pendingEffects, state.nextEffectId, result.effects);
      return {
        model: result.model,
        pendingEffects: appended.pendingEffects,
        nextEffectId: appended.nextEffectId,
      };
    }

    case 'EffectFinished': {
      return {
        ...state,
        pendingEffects: state.pendingEffects.filter((p) => p.id !== action.id),
      };
    }

    default:
      throw new Error('Unhandled program action');
  }
}

// =============================================================================
// Main program hook
// =============================================================================

/**
 * The main program hook — orchestrates state and effects.
 *
 * Key invariants:
 * - Each effect has its own AbortController
 * - Effects complete naturally (no shared cleanup that aborts siblings)
 * - EffectFinished message removes only that effect from the queue
 * - Unmount aborts all active effects
 * - When effects are removed from queue (e.g., new LoadStarted), their controllers are aborted
 * - Completed effects are removed from activeEffectsRef
 */
export function useCommentsProgram(
  props: UseCommentsProgramProps
): UseCommentsProgramResult {
  const { dependencies } = props;

  const [state, dispatch] = useReducer(programReducer, initialState);

  // Track active effects by their ID
  const activeEffectsRef = useRef<Map<number, TrackedEffect>>(new Map());

  // Dispatch domain message
  const dispatchDomain = useCallback((message: CommentsMsg) => {
    dispatch({ type: 'DomainMessage', message });
  }, []);

  // Process pending effects — reconcile with active map, start new ones, abort obsolete ones
  useEffect(() => {
    const pending = state.pendingEffects;
    const active = activeEffectsRef.current;
    const pendingIds = new Set(pending.map((p) => p.id));

    // Abort and remove active effects no longer in the pending queue (obsolete)
    for (const [id, tracked] of active) {
      if (!pendingIds.has(id)) {
        tracked.controller.abort();
        active.delete(id);
      }
    }

    // Start any pending effects not yet running
    for (const pe of pending) {
      if (active.has(pe.id)) {
        continue;
      }

      const controller = new AbortController();
      const context: CommentsEffectContext = {
        dispatch: (msg: CommentsMsg) => dispatchDomain(msg),
      };

      const promise = runEffect(pe.effect, context, dependencies, controller.signal);

      active.set(pe.id, { effect: pe.effect, controller, promise });

      // When this effect completes (naturally or via abort),
      // dispatch EffectFinished AND remove from active map
      void promise.finally(() => {
        // Remove from active map only if this is still the current controller
        if (activeEffectsRef.current.get(pe.id)?.controller === controller) {
          activeEffectsRef.current.delete(pe.id);
        }
        dispatch({ type: 'EffectFinished', id: pe.id });
      });
    }
  }, [state.pendingEffects, dependencies, dispatchDomain]);

  // Unmount cleanup: abort all active effects
  useEffect(() => {
    return () => {
      const active = activeEffectsRef.current;
      for (const [, tracked] of active) {
        tracked.controller.abort();
      }
      active.clear();
    };
  }, []);

  // --- Action callbacks ---

  const start = useCallback((postId: string, loadId: number) => {
    dispatchDomain({ type: 'LoadStarted', loadId, postId });
  }, [dispatchDomain]);

  const changeDraft = useCallback((draft: string) => {
    dispatchDomain({ type: 'DraftChanged', draft });
  }, [dispatchDomain]);

  const send = useCallback(() => {
    dispatchDomain({ type: 'SendClicked' });
  }, [dispatchDomain]);

  const like = useCallback((commentId: string) => {
    dispatchDomain({ type: 'LikeClicked', commentId });
  }, [dispatchDomain]);

  return {
    model: state.model,
    start,
    changeDraft,
    send,
    like,
  };
}